import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@/shared/utils";
import type { Assignee, TaskListItem } from "../../../types/api";
import type { ProjectBoardView } from "../../../hooks/useCrossProjectBoard";
import ProjectOverviewGrid from "./ProjectOverviewGrid";
import ProjectWorkspace from "./ProjectWorkspace";

interface OverviewTabContentProps {
  projectId: number | null;
  onSelectProject: (projectId: number) => void;
  projectBoards: ProjectBoardView[];
  activeAssignees: Assignee[];
  onTaskClick: (task: TaskListItem, projectId: number) => void;
}

export default function OverviewTabContent({
  projectId,
  onSelectProject,
  projectBoards,
  activeAssignees,
  onTaskClick,
}: OverviewTabContentProps) {
  const { i18n } = useTranslation();
  const locale = (i18n.language as Locale) || "ru";

  const activeBoard = useMemo(() => {
    if (projectId === null) return null;
    return projectBoards.find((b) => b.project.id === projectId) ?? null;
  }, [projectId, projectBoards]);

  const allTasks = useMemo(() => {
    if (!activeBoard) return [];
    const tasks: TaskListItem[] = [];
    for (const col of activeBoard.columns) {
      tasks.push(...col.tasks);
    }
    tasks.push(...activeBoard.backlogColumn.tasks);
    return tasks;
  }, [activeBoard]);

  const kpiTasks = useMemo(() => allTasks.filter((t) => t.task_type === "kpi"), [allTasks]);

  if (projectId === null || !activeBoard) {
    return <ProjectOverviewGrid projectBoards={projectBoards} onSelectProject={onSelectProject} />;
  }

  return (
    <ProjectWorkspace
      project={activeBoard.project}
      assignees={activeAssignees}
      allTasks={allTasks}
      kpiTasks={kpiTasks}
      locale={locale}
      onTaskClick={onTaskClick}
    />
  );
}
